import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import AuthContext from '../../../context/authContext';
import usePendingBookings from '../../../hooks/usePendingBookings';
import ResolutionResponsibleFields, {
  REASON_MIN_LENGTH,
} from './ResolutionResponsibleFields';

/**
 * Action for bookings in the "missingCheckIn" pending-attention section where the staff
 * forgot to record anything for a past booking. Lets the operator retroactively
 * set check-in, optional payment, checkout, and an optional rating in one step.
 */
function RetroactiveCompleteButton({
  booking,
  onBookingUpdated,
  variant = 'contained',
  size = 'small',
  fullWidth = false,
  sx = undefined,
}) {
  const authCtx = useContext(AuthContext);
  const { retroactiveComplete, fetchResolutionCandidates } = usePendingBookings();

  const [open, setOpen] = useState(false);
  const [markedPaid, setMarkedPaid] = useState('yes');
  const [reason, setReason] = useState('');
  const [responsibleUserId, setResponsibleUserId] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [candidatesError, setCandidatesError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canShow = useMemo(() => {
    if (!booking?._id || typeof onBookingUpdated !== 'function') return false;
    if (!authCtx.checkRightAny('internalBooking:special:resolveForgotten')) return false;
    if (booking.status !== 'confirmed') return false;
    if (booking.presentSince) return false;
    const start = booking?.startTime ? dayjs(booking.startTime) : null;
    if (!start?.isValid()) return false;
    return start.isBefore(dayjs().startOf('day'));
  }, [authCtx, booking, onBookingUpdated]);

  const hasUnpaidOfflinePayment = useMemo(() => {
    if (!booking || booking.fullyPaid) return false;
    const lineItems = Array.isArray(booking.lineItems) ? booking.lineItems : [];
    return lineItems.some(item => {
      if (!item || item.status !== 'active' || !(item.count > 0)) return false;
      const refs = Array.isArray(item.paymentReferences) ? item.paymentReferences : [];
      return refs.some(
        ref => ref && ref.paymentType === 'offline' && ref.count > 0 && !ref.isPaid
      );
    });
  }, [booking]);

  // Keep the latest fetcher in a ref so the effect below only re-runs on
  // open/booking changes — never on a mere identity change of the hook callback.
  const fetchResolutionCandidatesRef = useRef(fetchResolutionCandidates);
  fetchResolutionCandidatesRef.current = fetchResolutionCandidates;

  useEffect(() => {
    if (!open || !booking?._id) return undefined;
    let cancelled = false;
    setIsLoadingCandidates(true);
    setCandidatesError(null);
    fetchResolutionCandidatesRef
      .current(booking._id)
      .then(({ candidates: loaded }) => {
        if (cancelled) return;
        setCandidates(loaded);
        setResponsibleUserId(prev => {
          if (loaded.length === 1) return loaded[0].userId;
          if (prev && loaded.some(c => c.userId === prev)) return prev;
          return null;
        });
      })
      .catch(err => {
        if (cancelled) return;
        setCandidatesError(err);
        setCandidates([]);
        setResponsibleUserId(null);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoadingCandidates(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, booking?._id]);

  if (!canShow) return null;

  const handleOpen = event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    setOpen(true);
  };

  const handleClose = event => {
    event?.stopPropagation?.();
    if (isSubmitting) return;
    setOpen(false);
    setReason('');
  };

  const trimmedReason = reason.trim();
  const reasonValid = trimmedReason.length >= REASON_MIN_LENGTH;
  const responsibleValid = candidates.length <= 1 || Boolean(responsibleUserId);
  const submitDisabled =
    isSubmitting || isLoadingCandidates || !reasonValid || !responsibleValid;

  const handleSubmit = async event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (!booking?._id || submitDisabled) return;

    try {
      setIsSubmitting(true);
      const effectiveMarkedPaid = hasUnpaidOfflinePayment && markedPaid === 'yes';
      const toastMessage = `Die Nacherfassung für ${booking.firstName} ${booking.lastName} wurde gespeichert und kann nach Identifizierung abgeschlossen werden`;
      const successToastMessage = 'Buchung nachträglich abgeschlossen';
      const displayInfo = {
        Typ: 'Nachträgliche Buchungserfassung',
        Kunde: `${booking.firstName} ${booking.lastName}`,
        Buchungsnummer: booking.bookingNumber,
        Termin: dayjs(booking.startTime).format('DD.MM.YYYY HH:mm'),
        Zahlung: hasUnpaidOfflinePayment
          ? effectiveMarkedPaid
            ? 'Als bezahlt markieren'
            : 'Als unbezahlt belassen'
          : 'Bereits vollständig bezahlt',
        Bewertung: 'Neutral (pauschal)',
        Grund: trimmedReason,
      };
      const onSuccess = updated => {
        if (updated) onBookingUpdated(booking._id, updated);
      };
      const updatedBooking = await retroactiveComplete(
        booking._id,
        {
          markedPaid: effectiveMarkedPaid,
          reason: trimmedReason,
          responsibleUserId: responsibleUserId || null,
        },
        toastMessage,
        null,
        displayInfo,
        onSuccess,
        successToastMessage
      );
      if (updatedBooking) onBookingUpdated(booking._id, updatedBooking);
      setOpen(false);
      setReason('');
    } catch (_) {
      // Errors are handled by the hook/useHttp via toast.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button
        size={size}
        variant={variant}
        color='primary'
        fullWidth={fullWidth}
        onClick={handleOpen}
        data-ignore-card-click
        sx={{ whiteSpace: 'nowrap', ...sx }}
      >
        Nachträglich erfassen
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
        <DialogTitle>Buchung nachträglich erfassen</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant='body2' color='text.secondary'>
              Die Gruppe war da, aber es wurde kein Check-in, Auscheck- oder Zahlungsstatus erfasst.
              Mit dieser Aktion werden alle Zustände rückwirkend gesetzt:
            </Typography>
            <Box
              component='ul'
              sx={{ m: 0, pl: 3, color: 'text.secondary', fontSize: 14, lineHeight: 1.6 }}
            >
              <li>Check-in = geplante Startzeit</li>
              <li>Auschecken = geplante Endzeit</li>
              <li>Kundenstimmung pauschal als „neutral“ vermerkt</li>
              <li>Bewertungsmail wird automatisch an den Kunden versendet</li>
              {hasUnpaidOfflinePayment && <li>Zahlung gemäß Auswahl unten</li>}
            </Box>

            <ResolutionResponsibleFields
              candidates={candidates}
              isLoadingCandidates={isLoadingCandidates}
              candidatesError={candidatesError}
              responsibleUserId={responsibleUserId}
              onResponsibleChange={setResponsibleUserId}
              reason={reason}
              onReasonChange={setReason}
              disabled={isSubmitting}
            />

            {hasUnpaidOfflinePayment && (
              <Box>
                <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
                  Wurde offline bezahlt?
                </Typography>
                <RadioGroup
                  value={markedPaid}
                  onChange={event => setMarkedPaid(event.target.value)}
                >
                  <FormControlLabel
                    value='yes'
                    control={<Radio />}
                    label='Ja, als vollständig bezahlt markieren'
                  />
                  <FormControlLabel
                    value='no'
                    control={<Radio />}
                    label='Nein, offene Zahlung bleibt bestehen'
                  />
                </RadioGroup>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleSubmit}
            disabled={submitDisabled}
          >
            Jetzt nachträglich abschließen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default RetroactiveCompleteButton;
